import {
  Calendar,
  ArrowRight,
  MapPin,
  Clock,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Event } from "../../../api";
import { formatEventTime12h, formatTimeString12h } from "../../../utils";
import moment from "moment";
import "moment/locale/es";

const eventController = new Event();
moment.locale("es");

export function NewsSection() {
  const navigate = useNavigate();

  // Estado para controlar el mes/año que se muestra en el calendario
  const [currentDate, setCurrentDate] = useState(new Date());

  // Estados para la data de la API
  const [calendarData, setCalendarData] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tooltip único (posicionado con JS) que se muestra al hacer hover sobre un día
  const [hoverTooltip, setHoverTooltip] = useState(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    };
  }, []);

  const openDayTooltip = (e, dayEvents) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const tooltipWidth = 256;
    const margin = 8;
    const gap = 10;

    const cellCenterX = rect.left + rect.width / 2;
    let left = cellCenterX - tooltipWidth / 2;
    left = Math.max(margin, Math.min(left, viewportWidth - tooltipWidth - margin));
    const arrowLeft = Math.max(16, Math.min(cellCenterX - left, tooltipWidth - 16));

    const spaceBelow = viewportHeight - rect.bottom - gap - margin;
    const spaceAbove = rect.top - gap - margin;
    const placeAbove = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, placeAbove ? spaceAbove : spaceBelow);

    setHoverTooltip({
      events: dayEvents,
      left,
      arrowLeft,
      placeAbove,
      maxHeight,
      top: placeAbove ? null : rect.bottom + gap,
      bottom: placeAbove ? viewportHeight - rect.top + gap : null,
    });
  };

  const scheduleCloseTooltip = () => {
    closeTimerRef.current = setTimeout(() => setHoverTooltip(null), 150);
  };

  const cancelCloseTooltip = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  // Función para obtener los eventos del mes actual
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; // API espera 1-12
      const response = await eventController.getLandingEvents(year, month);

      if (response.success && response.data) {
        setCalendarData(response.data.calendar);
        setUpcomingEvents(response.data.upcomingEvents || []);
      }
    } catch (err) {
      console.error("Error fetching events:", err);
      setError("Error al cargar los eventos");
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  // Cargar eventos cuando cambia el mes
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Función para navegar entre meses
  const navigateMonth = (direction) => {
    setCurrentDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  // Obtener el nombre del mes actual
  const monthName = currentDate.toLocaleString("es-ES", { month: "long" });
  const year = currentDate.getFullYear();

  // Mapear eventos de la API al formato del calendario
  const calendarEvents =
    calendarData?.events?.map((event) => ({
      id: event.id,
      date: moment(event.eventDate).format('YYYY-MM-DD'),
      startDate: event.startDate ? moment(event.startDate).format('YYYY-MM-DD') : moment(event.eventDate).format('YYYY-MM-DD'),
      endDate: event.endDate ? moment(event.endDate).format('YYYY-MM-DD') : moment(event.eventDate).format('YYYY-MM-DD'),
      day: event.day,
      title: event.title,
      description: event.description,
      location: event.location,
      time:
        event.startTime && event.endTime
          ? `${formatTimeString12h(event.startTime)} - ${formatTimeString12h(event.endTime)}`
          : event.startTime
          ? formatTimeString12h(event.startTime)
          : "",
      category: event.category,
      slug: event.slug,
    })) || [];

  // Función para verificar si una fecha está dentro del rango del evento - Usar moment para todas las comparaciones
  const isDateInEventRange = (dateString, event) => {
    const checkDate = moment(dateString);
    const startDate = moment(event.startDate);
    const endDate = moment(event.endDate);

    // Comparar solo las fechas sin horas
    return checkDate.isSameOrAfter(startDate, 'day') && checkDate.isSameOrBefore(endDate, 'day');
  };

  // Generar el calendario del mes
  const generateCalendar = () => {
    const firstDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1
    );
    const lastDayOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      0
    );
    const startingDayOfWeek =
      firstDayOfMonth.getDay() === 0 ? 6 : firstDayOfMonth.getDay() - 1;

    const daysInMonth = lastDayOfMonth.getDate();
    const totalCells = Math.ceil((daysInMonth + startingDayOfWeek) / 7) * 7;

    const days = [];

    // Nombres de los días de la semana
    const weekDays = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

    // Añadir nombres de los días
    weekDays.forEach((day) => {
      days.push(
        <div
          key={`header-${day}`}
          className="text-center py-2 font-medium text-sm text-gray-500"
        >
          {day}
        </div>
      );
    });

    // Añadir celdas vacías para el inicio del mes
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="p-2 border border-gray-100">
          &nbsp;
        </div>
      );
    }

    // Añadir los días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      const dateString = moment(date).format('YYYY-MM-DD');

      // Buscar eventos para este día (incluye rangos de fechas)
      const dayEvents = calendarEvents.filter((event) => {
        return isDateInEventRange(dateString, event);
      });

      const hasEvents = dayEvents.length > 0;

      days.push(
        <div
          key={`day-${day}`}
          className={`p-1 border border-gray-100 min-h-[60px] relative cursor-pointer
            ${hasEvents ? "bg-primary-custom/5" : ""}`}
          onMouseEnter={(e) => hasEvents && openDayTooltip(e, dayEvents)}
          onMouseLeave={() => hasEvents && scheduleCloseTooltip()}
        >
          <div className="flex justify-between">
            <span
              className={`text-sm font-medium ${
                hasEvents ? "text-primary-custom" : "text-gray-700"
              }`}
            >
              {day}
            </span>
            {hasEvents && (
              <span
                className="h-2 w-2 rounded-full"
                style={{
                  backgroundColor: dayEvents[0].category?.color || "#4CAF50",
                }}
              ></span>
            )}
          </div>

          {hasEvents && (
            <div className="mt-1">
              <div className="text-xs truncate text-primary-custom">
                {dayEvents[0].title}
              </div>
              {dayEvents.length > 1 && (
                <div className="text-xs text-gray-500">
                  +{dayEvents.length - 1} más
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Añadir celdas vacías para completar la última semana
    const remainingCells = totalCells - (startingDayOfWeek + daysInMonth);
    for (let i = 0; i < remainingCells; i++) {
      days.push(
        <div key={`empty-end-${i}`} className="p-2 border border-gray-100">
          &nbsp;
        </div>
      );
    }

    return days;
  };

  // Función para obtener el color de fondo basado en el índice
  const getEventColor = (index) => {
    const colors = [
      "bg-blue-500",
      "bg-yellow-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-red-500",
    ];
    return colors[index % colors.length];
  };

  return (
    <section id="eventos" className="py-16">
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-primary-custom mb-4">
            Eventos
          </h2>
          <div className="w-24 h-1 bg-primary-custom mx-auto rounded-full mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Mantente informado sobre los últimos eventos y actividades en
            nuestra institución educativa.
          </p>
        </div>

        {/* Layout de dos columnas: Calendario a la izquierda, Noticias a la derecha */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Columna izquierda: Calendario */}
          <div className="w-full lg:w-7/12 order-2 lg:order-1">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-primary-custom">
                  Calendario de Eventos
                </h3>
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 border-gray-300 flex items-center justify-center"
                    onClick={() => navigateMonth(-1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-lg font-medium capitalize min-w-[140px] text-center">
                    {monthName} {year}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0 border-gray-300 flex items-center justify-center"
                    onClick={() => navigateMonth(1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Calendario de cuadrícula */}
              {loading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-custom" />
                </div>
              ) : error ? (
                <div className="text-center py-20 text-red-500">{error}</div>
              ) : (
                <div className="grid grid-cols-7 gap-1">
                  {generateCalendar()}
                </div>
              )}
            </div>
          </div>

          {hoverTooltip &&
            createPortal(
              <div
                className="fixed z-[9999] w-64 max-w-[calc(100vw-16px)] overflow-y-auto rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
                style={{
                  left: hoverTooltip.left,
                  top: hoverTooltip.top ?? undefined,
                  bottom: hoverTooltip.bottom ?? undefined,
                  maxHeight: hoverTooltip.maxHeight,
                }}
                onMouseEnter={cancelCloseTooltip}
                onMouseLeave={scheduleCloseTooltip}
              >
                <div
                  className={`absolute h-3 w-3 rotate-45 bg-white ${
                    hoverTooltip.placeAbove
                      ? "-bottom-1.5 border-b border-r border-gray-200"
                      : "-top-1.5 border-l border-t border-gray-200"
                  }`}
                  style={{ left: hoverTooltip.arrowLeft - 6 }}
                />
                {hoverTooltip.events.map((event) => (
                  <div
                    key={event.id}
                    className="mb-2 last:mb-0 cursor-pointer hover:bg-gray-50 rounded-md p-1 -m-1"
                    onClick={() => event.slug && navigate(`/events/${event.slug}`)}
                  >
                    <div
                      className="text-xs font-medium"
                      style={{ color: event.category?.color || "#4CAF50" }}
                    >
                      {event.category?.name || "Evento"}
                    </div>
                    <div className="font-medium text-sm">{event.title}</div>
                    <div className="text-xs text-gray-600 my-1">
                      {event.description}
                    </div>
                    {event.location && (
                      <div className="flex items-center text-xs text-gray-500">
                        <MapPin className="h-3 w-3 mr-1" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    {event.time && (
                      <div className="flex items-center text-xs text-gray-500">
                        <Clock className="h-3 w-3 mr-1" />
                        <span>{event.time}</span>
                      </div>
                    )}
                    {event.startDate !== event.endDate && (
                      <div className="flex items-center text-xs text-gray-500 mt-1">
                        <Calendar className="h-3 w-3 mr-1" />
                        <span>
                          {moment.utc(event.startDate).format("DD MMM")} -{" "}
                          {moment.utc(event.endDate).format("DD MMM")}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>,
              document.querySelector(".client-layout") || document.body
            )}

          {/* Columna derecha: Próximos eventos */}
          <div className="w-full lg:w-5/12 order-1 lg:order-2">
            <h3 className="text-xl font-bold text-primary-custom mb-6">
              Próximos eventos
            </h3>
            <div className="space-y-4">
              {loading ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="h-6 w-6 animate-spin text-primary-custom" />
                </div>
              ) : upcomingEvents.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                  No hay eventos próximos programados
                </div>
              ) : (
                upcomingEvents.slice(0, 3).map((item, index) => (
                  <Card
                    key={item.id}
                    className="bg-white border-l-4 hover:border-l-primary-custom transition-all duration-300 group hover:shadow-sm"
                    style={{
                      borderLeftColor: item.category?.color || "#4CAF50",
                    }}
                  >
                    <CardContent className="p-3 flex items-start gap-4">
                      <div
                        className={`${getEventColor(
                          index
                        )} p-1.5 rounded-md text-white self-center shrink-0`}
                        style={{ backgroundColor: item.category?.color }}
                      >
                        <Calendar className="h-6 w-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1.5">
                          <h3 className="font-medium text-base text-primary-custom">
                            {item.title}
                          </h3>
                          <div className="flex items-center text-xs text-gray-500">
                            <Calendar className="h-3 w-3 mr-1" />
                            {item.formattedDate} · {formatEventTime12h(item.eventDate)}
                          </div>
                        </div>
                        <div className="flex justify-between items-end">
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {item.description}
                          </p>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => item.slug && navigate(`/events/${item.slug}`)}
                            className="p-0 h-auto text-xs text-primary-custom hover:text-primary-custom/80 cursor-pointer"
                          >
                            <ArrowRight className="h-3 w-3 ml-1" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
            {/* <div className="text-center mt-6">
              <Button className="border-primary-custom bg-primary-custom text-white hover:bg-primary-custom hover:text-white">
                Ver todos los eventos
              </Button>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
}
